import { Label, TypographyPUI } from '@seen/ui'
import type { JSX, ReactNode } from 'react'
import type {
  Control,
  FieldPath,
  FieldPathValue,
  FieldValues,
} from 'react-hook-form'
import { ComboboxButton } from '#src/components/ComboboxButton.tsx'
import { FormField } from '#src/components/core/form/FormField.tsx'
import { SuggestionSourceExcerpt } from '#src/components/rich-text-editor/SuggestionSourceExcerpt.tsx'
import { ValidatedToolFieldControls } from '#src/components/validated-tool-form/shared/ValidatedToolFieldControls.tsx'
import type { ValidatedToolFieldSuggestion } from '#src/components/validated-tool-form/shared/validatedToolSuggestion.ts'

export interface ValidatedToolDropdownFieldProps<
  TFieldValues extends FieldValues = FieldValues,
  TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
> {
  control: Control<TFieldValues>
  name: TName
  options: {
    value: FieldPathValue<TFieldValues, TName>
    label: string
  }[]
  label: ReactNode
  className?: string
  /* Optional AI suggestion for THIS field. A dropdown has no per-option row to
   * anchor to, so the controls sit under the combobox — which is still the
   * control they decide, the placement rule the radio field follows. */
  suggestion?: ValidatedToolFieldSuggestion
}

export function ValidatedToolDropdownField<
  TFieldValues extends FieldValues = FieldValues,
  TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
>({
  control,
  name,
  options,
  label,
  className,
  suggestion,
}: ValidatedToolDropdownFieldProps<TFieldValues, TName>): JSX.Element {
  return (
    <FormField
      control={control}
      name={name}
      render={({ field }) => (
        <div className={className}>
          <Label htmlFor={name} className="flex flex-col gap-2" unstyled>
            <div className="flex items-center gap-2">
              <TypographyPUI className="font-semibold">{label}</TypographyPUI>
            </div>
            <div className="place-items-center">
              <ComboboxButton options={options} {...field} />
            </div>
          </Label>
          {suggestion && (
            <>
              {suggestion.status === 'pending' && (
                <SuggestionSourceExcerpt
                  citation={suggestion.citation}
                  sourceProvenance={suggestion.sourceProvenance}
                />
              )}
              <ValidatedToolFieldControls
                linkId={name}
                suggestion={suggestion}
              />
            </>
          )}
        </div>
      )}
    />
  )
}
